import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  Subject,
  combineLatest,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import {
  GradeLesson,
  GradeLessonStatus,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { SessionStatus } from '../../../../models/session.school.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolAttendanceService } from '../../../../services/attendance.school.service';
import { AuthService } from '../../../../services/auth.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as lessonActions from '../../../../state/actions/grade-lesson.school.actions';
import * as gradeActions from '../../../../state/actions/grade.school.actions';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as lessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { BtnTabComponent } from '../../../components/buttons/btn-tab/btn-tab.component';
import { DlgUpsertSessionComponent } from '../../sessions/dlg-upsert-session/dlg-upsert-session.component';

@Component({
  selector: 'app-detail-faculty',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnActionComponent,
    BtnTabComponent,
    BtnRouteComponent,
    RouterLink,
    FormsModule,
    CommonModule,
    AsyncPipe,
    NgxSkeletonLoaderModule,
    BtnRouteComponent,
    RouterOutlet,
  ],
  templateUrl: './detail-faculty.component.html',
  styleUrl: './detail-faculty.component.css',
})
export class DetailFacultyComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  roles: SchoolRolesService = inject(SchoolRolesService);
  private store: Store<AppState> = inject(Store<AppState>);
  logger: NGXLogger = inject(NGXLogger);
  attendanceService: SchoolAttendanceService = inject(SchoolAttendanceService);
  toastr: ToastrService = inject(ToastrService);
  utilsService: UtilsService = inject(UtilsService);
  private dialog: MatDialog = inject(MatDialog);
  authService: AuthService = inject(AuthService);

  user: UserModel | null = null;
  teacher: SchoolMember | null | undefined = null;

  lessons$: Observable<GradeLesson[] | null | undefined> = new Observable();
  group: Group | null | undefined;
  grade: Grade | null | undefined = undefined;

  groupId: string | null = null;
  facultyId: string | null = null;

  exit$ = new Subject<void>();

  private params$ = combineLatest({
    params: this.route.paramMap,
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => ({
      groupId: params.get('id'),
      facultyId: params.get('facultyId'),
      academicYear: queryParams.get('academicYear')
        ? Number(queryParams.get('academicYear'))
        : undefined,
    })),
    takeUntil(this.exit$),
  );

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  rolesType = SchoolRoles;

  myRoles$: Observable<any> = new Observable();

  inviteText: string =
    'Hi, I would like to invite you to join our school group on AtChurch. Please click on the link below to join. https://web.atchurch.app/links/groupinvite?groupId=';

  activeTab: string = 'sessions'; // Default active tab
  academicYears: number[] = [];
  academicYear: number | undefined;

  sessionStatus = SessionStatus;
  lessonStatus = GradeLessonStatus;
  utils = UtilsService;

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setActiveTab();
      });

    this.myRoles$ = this.store
      .select(groupSelector.getMyRolesInGroup(this.groupId))
      .pipe(takeUntil(this.exit$));

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnInit(): void {
    this.params$.subscribe(({ groupId, facultyId, academicYear }) => {
      this.groupId = groupId;
      this.facultyId = facultyId;

      // Now trigger all your data fetching in sequence based on these values
      this.loadData(groupId, facultyId, academicYear);

      this.inviteText = `Hi, I would like to invite you to join our school group '${this.group?.name}' on AtChurch. Please click on the link below to join. https://${environment.domain}/links/groupinvite?groupId=`;
    });
  }

  private loadData(
    groupId: string | null,
    facultyId: string | null,
    academicYear?: number,
  ) {
    this.logger.debug(
      `DetailFacultyComponent :: loadData called with groupId: ${groupId}, facultyId: ${facultyId}, academicYear: ${academicYear}`,
    );
    if (!groupId || !facultyId) return;

    // 2. Chain your logic so one piece of data leads to the next
    this.store
      .select(groupSelector.getGroupById(groupId))
      .pipe(take(1)) // Use take(1) if you just want the initial fetch logic
      .subscribe((group) => {
        this.group = group;
        this.academicYears = this.loadAcademicYears(group);

        // If URL didn't have academicYear, fallback to group default
        const effectiveYear = academicYear ?? group?.academicYear;
        this.academicYear = effectiveYear;

        // 3. Now fetch Grade and Lessons using the guaranteed year
        if (groupId && facultyId && effectiveYear)
          this.fetchGradeAndLessons(groupId, facultyId, effectiveYear);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  private fetchGradeAndLessons(
    groupId: string,
    facultyId: string,
    academicYear: number,
  ) {
    // --- 1. TEACHER & LESSONS FLOW ---
    this.store
      .select(memberSelector.getSchoolMemberById(facultyId, groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((teacher) => {
        this.teacher = teacher;

        if (!teacher) {
          this.logger.debug('Teacher missing, dispatching load teacher...');
          this.store.dispatch(
            memberActions.getMember({ groupId, memberId: facultyId }),
          );
          // We don't 'return' here anymore; we just let the next emission (when teacher arrives) handle the rest
        } else if (teacher.gradeId) {
          this.logger.debug(
            `Teacher found with gradeId: ${teacher.gradeId}. Checking lessons...`,
          );

          // Setup the lessons$ observable for the template
          this.lessons$ = this.store.select(
            lessonSelector.getGradeLessonsByGradeId({
              gradeId: teacher.gradeId,
              groupId: groupId,
              academicYear: academicYear,
              lessonStatus: [GradeLessonStatus.open, GradeLessonStatus.closed],
            }),
          );

          // Check if we need to trigger a fetch for lessons
          this.lessons$.pipe(takeUntil(this.exit$)).subscribe((lessons) => {
            if (!lessons || lessons.length === 0) {
              this.logger.debug('Lessons missing, dispatching load lessons...');
              this.store.dispatch(
                lessonActions.getGradeLessons({
                  groupId,
                  gradeId: teacher.gradeId!,
                  status: [GradeLessonStatus.open, GradeLessonStatus.closed],
                  academicYear,
                }),
              );
            }
          });
        }
      });

    // --- 2. GRADE & SESSION STATS FLOW ---
    // We pipe off the Teacher so we HAVE the gradeId before asking for the Grade
    this.store
      .select(memberSelector.getSchoolMemberById(facultyId, groupId))
      .pipe(
        filter((t) => !!t?.gradeId), // Wait until the teacher (and their gradeId) exists
        switchMap((t) =>
          this.store.select(
            gradeSelector.getGradeByTeacherId({
              teacherId: facultyId,
              groupId,
              academicYear,
            }),
          ),
        ),
        takeUntil(this.exit$),
      )
      .subscribe((grade) => {
        this.grade = grade;

        if (!grade && this.teacher?.gradeId) {
          this.logger.debug('Grade missing, dispatching load grade...');
          this.store.dispatch(
            gradeActions.getGrade({
              groupId,
              gradeYear: academicYear,
              gradeId: this.teacher.gradeId,
            }),
          );
        }
      });
  }

  loadAcademicYears(g: Group | null | undefined): number[] {
    if (!g?.academicYear) return [];

    const years: number[] = [];

    const academicYear = g?.academicYear;

    for (let year = academicYear; year >= academicYear - 2; year--) {
      years.push(year);
    }
    this.logger.debug(
      `DetailFacultyComponent :: loadAcademicYears :: years: ${JSON.stringify(years)}`,
    );
    return years;
  }

  // isLessonViewable(publishBy?: Timestamp): boolean {
  //   if (!publishBy) return false;
  //   const currentTime = Timestamp.now();
  //   return publishBy.toMillis() <= currentTime.toMillis();
  // }

  // addMoreSessions() {
  //   // console.log(
  //   //   `DetailFacultyComponent :: addMoreSessions : ${this.groupId}, ${this.grade?.id}, ${this.facultyId}`
  //   // );
  //   if (
  //     this.groupId === null ||
  //     this.academicYear == undefined ||
  //     !this.grade?.id ||
  //     this.facultyId === null
  //   )
  //     return;
  //   // console.log(`DetailFacultyComponent :: addMoreSessions`);
  //   this.sessions$.pipe(take(1)).subscribe((sessions) => {
  //     // console.log(
  //     //   `DetailFacultyComponent :: Sessions length: ${sessions?.length}`
  //     // );
  //     this.store.dispatch(
  //       sessionActions.getSessions({
  //         groupId: this.groupId!,
  //         academicYear: this.academicYear!,
  //         teacherId: this.facultyId!,
  //         lastRecord:
  //           (sessions ?? [])?.length > 0
  //             ? sessions![sessions!.length - 1]?.startDate
  //             : undefined,
  //       }),
  //     );
  //   });
  // }

  // addMoreLessons() {
  //   if (this.groupId === null || this.grade?.id === null) return;
  //   this.lessons$.pipe(take(1)).subscribe((lessons) => {
  //     this.store.dispatch(
  //       lessonActions.getGradeLessons({
  //         groupId: this.groupId!,
  //         gradeId: this.grade?.id!,
  //         academicYear: this.academicYear!,
  //         status: [GradeLessonStatus.open, GradeLessonStatus.closed],
  //         lastRecord:
  //           (lessons ?? [])?.length > 0
  //             ? lessons![lessons!.length - 1]?.modified
  //             : undefined,
  //       }),
  //     );
  //   });
  // }

  // onRefreshLessons() {
  //   if (this.groupId === null || this.grade?.id === null) return;
  //   this.store.dispatch(
  //     lessonActions.getGradeLessons({
  //       groupId: this.groupId!,
  //       gradeId: this.grade?.id!,
  //       academicYear: this.academicYear!,
  //       status: [GradeLessonStatus.open, GradeLessonStatus.closed],
  //     }),
  //   );
  // }

  /**
   * Opens a dialog to take attendance for a specific session.
   * @param item The session for which attendance is to be taken.
   */
  // onSessionAttendance(item: Session) {
  //   if (
  //     item.id === undefined ||
  //     !this.grade?.id ||
  //     !this.groupId ||
  //     !this.group?.academicYear
  //   ) {
  //     this.toastr.error(
  //       'Required data missing. Kindly login again',
  //       'Error taking Attendance',
  //     );
  //     // return;
  //   }

  //   let dialogConfig = new MatDialogConfig();

  //   dialogConfig.data = {
  //     title: 'Update Attendance',
  //     description: `Update attendance for session '${item.sessionName}'`,
  //     action: 'Update',
  //     groupId: this.groupId,
  //     academicYear: this.group?.academicYear,
  //     sessionId: item.id,
  //     gradeId: this.grade?.id,
  //     students$: this.students$,
  //   };

  //   const dialogRef = this.dialog.open(
  //     DlgSessionAttendanceComponent,
  //     dialogConfig,
  //   );

  //   return dialogRef.afterClosed().subscribe((result) => {
  //     if (result) {
  //       const session: Session = {
  //         sessionStatus: SessionStatus.active,
  //       } as Session;

  //       return this.store.dispatch(
  //         sessionActions.update({
  //           groupId: this.groupId!,
  //           sessionId: item.id!,
  //           session: session,
  //         }),
  //       );
  //     }
  //     return;
  //   });
  // }

  // onEditLesson(item: GradeLesson) {
  //   // console.log(`DetailFacultyComponent :: onEditLesson called`);
  //   if (!this.groupId || this.grade?.id === undefined) return;

  //   let dialogConfig = new MatDialogConfig();

  //   dialogConfig.data = {
  //     title: 'Update Grade Lesson',
  //     description: 'Update grade lesson details',
  //     action: 'Update',
  //     lesson: item,
  //   };

  //   const dialogRef = this.dialog.open(
  //     DlgUpsertGradeLessonComponent,
  //     dialogConfig,
  //   );
  //   return dialogRef.afterClosed().subscribe((result) => {
  //     if (result) {
  //       this.toastr.success(
  //         'The grade lesson details have been updated successfully.',
  //         'Grade Lesson Updated',
  //       );
  //     }
  //     return;
  //   });
  // }

  async onAddSession() {
    if (this.group === null) return;

    if (this.grade?.id === undefined) {
      this.utilsService.onShowAlert(
        {
          title: 'Grade Not Found',
          message: `Grade not assigned to faculty member. Please assign grade and try again.`,
        } as AppExceptionModel,
        false,
      );
      return;
    }

    let dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];

    dialogConfig.data = {
      title: 'Create Session',
      description: `Create a new session for '${this.grade?.gradeName}' grade`,
      action: 'Create',
      groupId: this.groupId,
      grade: this.grade,
      user: this.user,
      lessons$: this.lessons$,
      isAssignable: true,
    };

    const dialogRef = this.dialog.open(DlgUpsertSessionComponent, dialogConfig);
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Session created successfully', '', {
          timeOut: 3000,
        });
      }
    });
  }

  // onEditSession(item: Session) {
  //   if (!this.groupId || this.grade?.id === undefined) return;
  //   let dialogConfig = new MatDialogConfig();

  //   dialogConfig.data = {
  //     title: 'Update Session',
  //     description: `Update session '${item.sessionName}' details`,
  //     action: 'Update',
  //     sessionName: item.sessionName,
  //     startDate: item.startDate.toDate(),
  //     endDate: item.endDate.toDate(),
  //     isAssignable: true,
  //     defaultLessonId: item.lessonId,
  //     defaultLessonName: item.lessonName,
  //     defaultTeacherId: item.teacherId,
  //     faculty$: this.faculty$,
  //     lessons$: this.lessons$,
  //   };

  //   const dialogRef = this.dialog.open(DlgUpsertSessionComponent, dialogConfig);
  //   return dialogRef.afterClosed().subscribe((result) => {
  //     if (result && result.sessionName !== undefined && this.groupId) {
  //       const session: Session = {
  //         ...item,
  //         sessionName: result.sessionName,
  //         gradeId: item.gradeId,
  //         gradeName: this.grade?.gradeName,
  //         academicYear: this.group?.academicYear,
  //         startDate: FireTimestamp.fromDate(moment(result.startDate).toDate()),
  //         endDate: FireTimestamp.fromDate(moment(result.endDate).toDate()),
  //         teacherId: result.teacherId ?? item.teacherId,
  //         teacherName: result.teacherName ?? item.teacherName,
  //         teacherPhotoUrl: result.teacherPhotoUrl ?? '',
  //         lessonId: result.lessonId,
  //         lessonName: result.lessonName,
  //       } as Session;

  //       return this.store.dispatch(
  //         sessionActions.update({
  //           groupId: this.groupId,
  //           sessionId: item.id!,
  //           session: session,
  //         }),
  //       );
  //     }
  //     return;
  //   });
  // }

  // onDeleteSession(item: Session) {
  //   let dialogConfig = new MatDialogConfig();

  //   dialogConfig.data = {
  //     title: 'Delete Session',
  //     description: `Are you sure you want to delete session '${item.sessionName}'?`,
  //     action: 'Delete',
  //     buttonColor: 'bg-red-500',
  //     showType: AlertType.error,
  //   };

  //   const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

  //   return dialogRef.afterClosed().subscribe((result) => {
  //     if (result && item.groupId) {
  //       return this.store.dispatch(
  //         sessionActions.deleteAction({ groupId: item.groupId, id: item.id! }),
  //       );
  //     }
  //     return;
  //   });
  // }

  // onNewAssignment() {
  //   if (
  //     this.group === null ||
  //     this.grade?.id === undefined ||
  //     this.grade.id === undefined
  //   )
  //     return;

  //   let dialogConfig = new MatDialogConfig();

  //   dialogConfig.data = {
  //     title: 'Create Assignment',
  //     description: `Create a new assignment for '${this.grade?.gradeName}' grade`,
  //     action: 'Create',
  //     lessons$: this.lessons$,
  //     groupId: this.group?.id,
  //     gradeId: this.grade.id,
  //     academicYear: this.group?.academicYear,
  //   };

  //   const dialogRef = this.dialog.open(
  //     DlgUpsertAssignmentComponent,
  //     dialogConfig,
  //   );
  //   return dialogRef.afterClosed().subscribe((result) => {
  //     if (result) {
  //       this.router.navigate(['assignments', result]);
  //     }
  //     return;
  //   });
  // }

  // onToggleLessonStatus(item: GradeLesson) {
  //   return this.store.dispatch(
  //     lessonActions.update({
  //       groupId: item.groupId!,
  //       lessonId: item.id!,
  //       gradeLesson: {
  //         lessonStatus:
  //           item.lessonStatus == GradeLessonStatus.closed
  //             ? GradeLessonStatus.open
  //             : GradeLessonStatus.closed,
  //       },
  //     }),
  //   );
  // }

  // onDeleteLesson(item: GradeLesson) {
  //   let dialogConfig = new MatDialogConfig();

  //   dialogConfig.data = {
  //     title: 'Delete Lesson',
  //     description: `Are you sure you want to delete lesson '${item.lessonName}'?`,
  //     action: 'Delete',
  //     buttonColor: 'bg-red-500',
  //     showType: AlertType.error,
  //   };

  //   const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

  //   return dialogRef.afterClosed().subscribe((result) => {
  //     if (result && item.groupId) {
  //       return this.store.dispatch(
  //         lessonActions.deleteAction({ groupId: item.groupId, id: item.id! }),
  //       );
  //     }
  //     return;
  //   });
  // }

  setActiveTab() {
    const url = this.router.url;
    if (url.includes('lessons')) this.activeTab = 'lessons';
    else if (url.includes('sessions')) this.activeTab = 'sessions';
    else if (url.includes('assignments')) this.activeTab = 'assignments';
    else if (url.includes('overview')) this.activeTab = 'overview';
    else if (url.includes('archive')) this.activeTab = 'archive';
    else this.activeTab = 'sessions';
  }

  onAcademicYearChange(event: any) {
    const year = event.target.value;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { academicYear: year },
      queryParamsHandling: 'merge',
    });
  }
}
